import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CvHealthLatestEnvelope,
  CvHealthReport,
  CvHealthReportEnvelope,
  CvHealthTargetKey,
} from "@tabbio/types";
import {
  getLatestCvHealthReport,
  isCvHealthFeatureDisabled,
  startCvHealthReview,
} from "../lib/cv-health-api";
import {
  createCvHealthAbandonedRunStore,
  createCvHealthAutoRecheckStore,
  createCvHealthScoreHistory,
  cvHealthQueryKey,
  deriveCvHealthDockModel,
  isCvHealthTargetSupported,
  toCvHealthTargetKey,
  type CvHealthDockModel,
} from "../lib/cv-health-editor";
import { useCVData } from "../lib/cv-mock-data";
import { trackProductEvent } from "../lib/product-analytics";

/**
 * The latest CV Health report of the CV the editor has open, for the dock
 * control and the editor sheet (both read the same cache entry).
 *
 * The query key sits under `['cv', target]`, so every CV edit (which runs
 * `invalidateCvDocumentQueries`) refetches it and the report turns stale.
 * Opening the editor or the sheet never starts a review. One path does: the
 * dock passes `autoRecheck` while the CV screen is focused and the app is in
 * the foreground, and a stale report then starts exactly one `editor_recheck`
 * for the new revision, so the dock reads "Checking…" and then the current
 * count instead of sitting on "Check again". "Check again" is left for when
 * that automatic review could not start.
 *
 * Falls back quietly: a delegated target is unsupported, and a server that
 * answers `FEATURE_DISABLED` (or any other error) leaves `ready` false, so
 * the dock keeps its setup ring and the feature stays invisible.
 */

type CvHealthQueryData =
  | { kind: "ready"; latest: CvHealthLatestEnvelope }
  | { kind: "disabled" };

/** "No report yet" arrives either as `report: null` or as NOT_FOUND. */
const EMPTY_LATEST: CvHealthLatestEnvelope = {
  report: null,
  currentRevisionHash: "",
  isStale: false,
};

/** While a review runs, poll the latest report so the dock settles by itself. */
const RUNNING_POLL_MS = 1500;

/** Shared by every mounted copy of the hook: one automatic review per revision. */
const autoRechecks = createCvHealthAutoRecheckStore();
/** Running reports the editor sheet gave up following, so the dock agrees with it. */
const abandonedRuns = createCvHealthAbandonedRunStore();
/** The previous score per target, for "Up from 61". */
const scoreHistory = createCvHealthScoreHistory();

async function fetchLatest(target: CvHealthTargetKey): Promise<CvHealthQueryData> {
  const response = await getLatestCvHealthReport(target);
  if (isCvHealthFeatureDisabled(response)) return { kind: "disabled" };
  if (response.error?.code === "NOT_FOUND") return { kind: "ready", latest: EMPTY_LATEST };
  if (response.error || !response.data) {
    throw new Error(response.error?.code ?? "CV_HEALTH_LATEST_FAILED");
  }
  return { kind: "ready", latest: response.data };
}

export interface UseCvHealthOptions {
  /**
   * Recheck a stale report on its own (once per revision). Only the dock sets
   * it, and only while the CV screen is focused and the app is active.
   */
  autoRecheck?: boolean;
}

export interface UseCvHealthResult {
  /** False for delegated targets: the dock keeps its ring. */
  supported: boolean;
  /** True once the latest report (or its absence) is known and the feature is on. */
  ready: boolean;
  /** The server said FEATURE_DISABLED. */
  disabled: boolean;
  isLoading: boolean;
  target: CvHealthTargetKey;
  latest: CvHealthLatestEnvelope | null;
  report: CvHealthReport | null;
  isStale: boolean;
  /** The automatic review of the current revision could not start: offer "Check again". */
  autoRecheckFailed: boolean;
  /** Score of the report before this one (same CV), or null. */
  previousScore: number | null;
  dock: CvHealthDockModel;
  refetch: () => void;
  /** Put a report the client already has (a review run, an apply, an undo) into the cache. */
  write: (envelope: CvHealthReportEnvelope | CvHealthLatestEnvelope) => void;
  /** Swap the cached report only (a decision returns the report, not the envelope). */
  writeReport: (report: CvHealthReport) => void;
  /** The editor sheet gave up following this running report: the dock stops saying "Checking…". */
  abandonRun: (reportId: string) => void;
}

export function useCvHealth({ autoRecheck = false }: UseCvHealthOptions = {}): UseCvHealthResult {
  const { target: editorTarget } = useCVData();
  const { i18n } = useTranslation();
  const locale: "en" | "ar" = i18n.language?.startsWith("ar") ? "ar" : "en";
  const queryClient = useQueryClient();
  const supported = isCvHealthTargetSupported(editorTarget);
  const target = toCvHealthTargetKey(editorTarget);
  const queryKey = useMemo(() => cvHealthQueryKey(editorTarget), [editorTarget]);

  const query = useQuery<CvHealthQueryData>({
    queryKey,
    queryFn: () => fetchLatest(target),
    enabled: supported,
    staleTime: 30_000,
    retry: 1,
    refetchInterval: (current) => {
      const data = current.state.data;
      return data?.kind === "ready" && data.latest.report?.status === "running"
        ? RUNNING_POLL_MS
        : false;
    },
  });

  const data = query.data;
  const disabled = data?.kind === "disabled";
  const latest = data?.kind === "ready" ? data.latest : null;
  // Known once any fetch succeeded; a later failed refetch keeps the last
  // answer rather than flipping the dock back to the ring.
  const ready = supported && !!latest;

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    void refetchQuery();
  }, [refetchQuery]);

  const write = useCallback(
    (envelope: CvHealthReportEnvelope | CvHealthLatestEnvelope) => {
      queryClient.setQueryData<CvHealthQueryData>(queryKey, {
        kind: "ready",
        latest: {
          report: envelope.report,
          currentRevisionHash: envelope.currentRevisionHash,
          isStale: envelope.isStale,
        },
      });
    },
    [queryClient, queryKey],
  );

  const writeReport = useCallback(
    (report: CvHealthReport) => {
      queryClient.setQueryData<CvHealthQueryData>(queryKey, (previous) =>
        previous?.kind === "ready"
          ? { kind: "ready", latest: { ...previous.latest, report } }
          : previous,
      );
    },
    [queryClient, queryKey],
  );

  // ── Automatic recheck of a changed CV ────────────────────────────────
  const autoEntry = useSyncExternalStore(
    autoRechecks.subscribe,
    () => autoRechecks.get(target),
    () => autoRechecks.get(target),
  );
  const report = latest?.report ?? null;
  // The revision to recheck: the CV changed and no review is running (a
  // running one settles first; if the CV moved on meanwhile, it goes stale
  // again and this picks the newest revision).
  const staleHash =
    supported && latest?.isStale && report && report.status !== "running"
      ? latest.currentRevisionHash
      : "";
  const writeRef = useRef(write);
  writeRef.current = write;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!autoRecheck || !staleHash) return;
    if (!autoRechecks.claim(target, staleHash)) return;
    trackProductEvent("cv_health_review_started", {
      entry_point: "editor",
      trigger: "editor_recheck",
      automatic: true,
    });
    void startCvHealthReview({ target, trigger: "editor_recheck", locale }).then(
      (response) => {
        if (isCvHealthFeatureDisabled(response)) {
          autoRechecks.settle(target, staleHash, "failed");
          refetchRef.current();
          return;
        }
        if (response.error || !response.data) {
          autoRechecks.settle(target, staleHash, "failed");
          return;
        }
        autoRechecks.settle(target, staleHash, "started");
        // A running report: the latest query polls it until it settles.
        writeRef.current(response.data);
      },
      () => autoRechecks.settle(target, staleHash, "failed"),
    );
  }, [autoRecheck, locale, staleHash, target]);

  const autoRecheckFailed =
    !!latest?.isStale &&
    autoEntry?.hash === latest.currentRevisionHash &&
    autoEntry.status === "failed";

  const previousScore = useMemo(() => scoreHistory.observe(target, report), [report, target]);

  const abandonedRunId = useSyncExternalStore(
    abandonedRuns.subscribe,
    () => abandonedRuns.get(target),
    () => abandonedRuns.get(target),
  );
  const runAbandoned =
    report?.status === "running" && abandonedRunId !== null && abandonedRunId === report.id;
  /** The sheet stopped following this running report (see `abandonedRuns`). */
  const abandonRun = useCallback(
    (reportId: string) => abandonedRuns.mark(target, reportId),
    [target],
  );

  const dock = useMemo(
    () => deriveCvHealthDockModel(latest, { autoRecheckFailed, runAbandoned }),
    [autoRecheckFailed, latest, runAbandoned],
  );

  return {
    supported,
    ready,
    disabled,
    isLoading: supported && query.isLoading,
    target,
    latest,
    report,
    isStale: latest?.isStale ?? false,
    autoRecheckFailed,
    previousScore,
    dock,
    refetch,
    write,
    writeReport,
    abandonRun,
  };
}
